import React from 'react';
import { IUIUrval } from '../../../../../../../../Types/UrvalTypes';
import {
  IColumn,
  ISumData,
} from '../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ILocalAnalys } from '../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { ISelectedAnalys } from '../../../EkonomiskAnalysPage';
import Logger, { namedError } from '../../../../../../../../Utils/Logger';
import { IUserInfo } from '../../../Utils/ekonomiskAnalysInitQueryUtils';
import {
  budgetRadSummationMode,
  BudgetSummationModeType,
  SummationModeType,
  verifikationSummationMode,
} from '../../../Utils/SummationModeUtils';

interface IEkonomiskAnalysSideMenuContext {
  newAnalysSettings: ILocalAnalys;
  setUrval: (newUrval: IUIUrval) => void;
  onSumsChanged: (sums: ISumData[], urval: IUIUrval) => void;
  getPeriodsComponent: (
    dense?: boolean,
    shouldUpdateDirect?: boolean,
    simple?: boolean
  ) => React.ReactNode;
  onColumnsChanged: (columns: IColumn[]) => void;
  denomination: number;
  onDenominationChange: (denomination: number) => void;
  nrDecimals: number;
  decimalsChanged: (decimals: number) => void;
  showComparison: boolean;
  toggleShowComparison: () => void;
  changeIncludeEndastBokfört: () => void;
  selectedAnalys: ISelectedAnalys | null;
  codeSystemId?: string;
  onChipSelectionChanged: (expandedLevel: number) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  analysSelectListRef: React.RefObject<HTMLDivElement | null>;
  analysSelectOpen: boolean;
  periodLocked: boolean;
  expandedSumLevel: number;
  createNewAnalys: (
    analys: ILocalAnalys,
    selectedAnalys: ISelectedAnalys
  ) => void;
  isLopandeAnalys: boolean;
  userInfo?: IUserInfo;
  onSummationModeChanged: (summationMode: SummationModeType) => void;
  onBudgetSummationModeChanged: (
    summationMode: BudgetSummationModeType
  ) => void;
  onExkluderaPersonalkostnaderChanged: (exkludera: boolean) => void;
  onVisaSummeringarChanged: (show: boolean) => void;
}

export const EkonomiskAnalysSideMenuContext =
  React.createContext<IEkonomiskAnalysSideMenuContext>({
    newAnalysSettings: {
      loading: false,
      name: '',
      type: 'RAPPORTDIMENSIONLOPANDE',
      sums: [],
      columns: [],
      dates: {
        from: { year: 2019, month: 1, day: 1, userString: '' },
        to: { year: 2019, month: 1, day: 1, userString: '' },
      },
      urval: null,
      other: {
        budgetSummationMode: budgetRadSummationMode,
        includeEjBokfordaJournaler: false,
        denomination: 1,
        decimals: 0,
        showComparison: false,
        summationMode: verifikationSummationMode, // { value: 'verifikation', displayName: 'Verifikation' },
        exkluderaPersonalKostnader: false,
        expandedSumIndex: 0,
      },
      defaultSettings: {
        decimals: 0,
        denomination: 1,
        includeEjBokfordaJournaler: false,
        showComparison: false,
        summationMode: verifikationSummationMode, // { value: 'verifikation', displayName: 'Verifikation' },
        exkluderaPersonalKostnader: false,
        visaSummeringar: true,
        budgetSummationMode: budgetRadSummationMode,
      },
      expandToIndex: 0,
      anvandsFor: [],
      isInterval: false,
      sparatAnvandarUrval: null,
      lopsaldoDetaljrad: false,
      ai_prompt: null,
      detaljanalys: false,
      visaSummeringar: true,
    },
    setUrval: () => {
      Logger.warn(
        namedError(
          'EkonomiskAnalysSideMenuContext-SetUrvalNotDefined',
          'Set urval not defined. EkonomiskAnalysSideMenuConsumer is used outside of EkonomiskAnalysSideMenuProvider'
        )
      );
    },
    onSumsChanged: () => {},
    getPeriodsComponent: () => {
      Logger.warn(
        namedError(
          'EkonomiskAnalysSideMenuContext-getPeriodComponentUsesDefault',
          'getPeriods component uses default, SideMenuContext used outside provider'
        )
      );
      return null;
    },
    onColumnsChanged: () => {},
    denomination: 0,
    onDenominationChange: () => {},
    nrDecimals: 0,
    decimalsChanged: () => {},
    showComparison: false,
    toggleShowComparison: () => {},
    changeIncludeEndastBokfört: () => {},
    selectedAnalys: null,
    onChipSelectionChanged: () => {},
    /** Used to scroll listitems into view */
    scrollContainerRef: React.createRef<HTMLDivElement>(),
    analysSelectListRef: React.createRef<HTMLDivElement>(),
    analysSelectOpen: false,
    periodLocked: true,
    expandedSumLevel: 0,
    createNewAnalys: () => {},
    isLopandeAnalys: true,
    userInfo: undefined,
    onBudgetSummationModeChanged: () => {},
    onSummationModeChanged: () => {},
    onExkluderaPersonalkostnaderChanged: () => {},
    onVisaSummeringarChanged: () => {},
  });

export const EkonomiskAnalysSideMenuProvider =
  EkonomiskAnalysSideMenuContext.Provider;
